import { Box, Text, Flex, Icon, Button } from '@chakra-ui/react';
import { GmailButton } from '@/components/contacts/intro-vouch/intro/GmailButton';
import { Microsoft } from '@/assets/icons/mailbox';
import { GoogleCalendar, OutlookCalendar } from '@/assets/icons/calendar';

import { ConnectWithMsForm } from '@/components/mailbox/ConnectWithMsForm';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { ConnectWithCalForm } from '@/components/calendar/ConnectWithCalForm';
import { useKFlags } from '@/hooks/use-k-flags';
import { InfoCircle } from '@kula-ai/icons-core';

export const GoogleReconnectNudge = () => {
  return (
    <Flex
      bg="rgba(0, 67, 206, 0.1)"
      px="24px"
      py="8px"
      alignItems="center"
      data-test-id="google-reconnect-nudge"
    >
      <Flex flex="1">
        <Icon as={InfoCircle} width="20px" height="20px" color="#0043CE" />
        <Box ml="12px">
          <Text fontWeight="semiBold" fontSize="14px">
            [Important] Gmail disconnected from Kula.
          </Text>
          <Text>
            All your Flows are paused. Connect your Google account to Kula to
            resume sending messages.
          </Text>
        </Box>
      </Flex>
      <GmailButton text="Reconnect your Google account" />
    </Flex>
  );
};

export const MicrosoftReconnectNudge = () => {
  const currUser = useSelector(selectCurrUser);
  const dispatch = useDispatch();
  return (
    <Flex
      bg="rgba(0, 67, 206, 0.1)"
      px="24px"
      py="8px"
      alignItems="center"
      data-test-id="microsoft-reconnect-nudge"
    >
      <Flex flex="1">
        <Icon as={InfoCircle} width="20px" height="20px" color="#0043CE" />
        <Box ml="12px">
          <Text fontWeight="semiBold" fontSize="14px">
            [Important] Outlook disconnected from Kula.
          </Text>
          <Text>
            All your Flows are paused. Connect your Microsoft account to Kula to
            resume sending messages.
          </Text>
        </Box>
      </Flex>
      <ConnectWithMsForm user={currUser} dispatch={dispatch}>
        <Button type="submit" variant="secondary" leftIcon={<Microsoft />}>
          Reconnect your Microsoft account
        </Button>
      </ConnectWithMsForm>
    </Flex>
  );
};

export const CalendarReconnectNudge = ({
  variant = 'gcal',
}: {
  variant?: 'gcal' | 'mscal';
}) => {
  const currUser = useSelector(selectCurrUser);
  const dispatch = useDispatch();
  const { tGoogleMeetExternalHost } = useKFlags();
  // Reconnecting Google Calendar must additively re-grant the Meet-host scope
  // when the feature is on, so a host doesn't silently lose host capability.
  const oauthFor =
    variant === 'gcal' && tGoogleMeetExternalHost
      ? 'calendar,meet'
      : 'calendar';
  return (
    <Flex
      bg="rgba(0, 67, 206, 0.1)"
      px="24px"
      py="8px"
      alignItems="center"
      data-test-id="cal-reconnect-nudge"
    >
      <Flex flex="1">
        <Icon as={InfoCircle} width="20px" height="20px" color="#0043CE" />
        <Box ml="12px">
          <Text fontWeight="semiBold" fontSize="14px">
            [Important] {variant === 'gcal' ? 'Google' : 'Microsoft'}{' '}
            disconnected from Kula.
          </Text>
          <Text>
            Integrate your Calendar with Kula for seamless service resumption.
          </Text>
        </Box>
      </Flex>
      <ConnectWithCalForm
        variant={variant}
        user={currUser}
        dispatch={dispatch}
        oauthFor={oauthFor}
      >
        <Button
          type="submit"
          variant="secondary"
          leftIcon={
            variant === 'gcal' ? (
              <GoogleCalendar width="24px" />
            ) : (
              <OutlookCalendar width="24px" />
            )
          }
        >
          Reconnect your {variant === 'gcal' ? 'Google' : 'Microsoft'} Calendar
        </Button>
      </ConnectWithCalForm>
    </Flex>
  );
};
